import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, Puzzle } from "lucide-react";
import Reveal from "@/components/Reveal";
import EmberCanvas from "@/components/EmberCanvas";
import { DEPLOY_PIPELINE, PLUGIN_PILLARS, PLUGIN_SUITE, RUST_NETWORK } from "@/data/content";

const Plugins = () => (
  <div className="relative pt-32">
    <div
      className="pointer-events-none absolute inset-x-0 top-0 h-[520px]"
      style={{
        background: "radial-gradient(ellipse 60% 100% at 50% 0%, rgba(229,46,46,0.08) 0%, transparent 70%)",
      }}
    />
    <header className="relative mx-auto max-w-[1400px] px-6 lg:px-10">
      <Reveal>
        <p className="font-mono text-xs tracking-[0.35em] text-[#E52E2E]">THE FOUNDRY // PLUGIN DEVELOPMENT</p>
        <h1 className="mt-5 max-w-4xl font-display text-5xl font-black uppercase leading-[0.95] tracking-tight text-[#F4F5F7] sm:text-7xl lg:text-8xl">
          Forged for Rust.<br />Built for Arsenal Servers.
        </h1>
      </Reveal>
      <Reveal delay={0.15}>
        <p className="mt-8 max-w-2xl text-base leading-relaxed text-[#9EA4B0] sm:text-lg">
          Before we forge worlds, we forge the systems that run them. The majority of the studio's
          craft today is custom Oxide plugin development — a full proprietary suite powering every
          server on the Arsenal Rust network, from competitive statistics to network-wide clans.
        </p>
        <div className="mt-8 grid grid-cols-3 gap-px border border-[#282D37] bg-[#282D37] sm:max-w-xl">
          {[
            ["ECOSYSTEM", "OXIDE"],
            ["GAME", "RUST"],
            ["DEPLOYED", "NETWORK-WIDE"],
          ].map(([k, v]) => (
            <div key={k} className="bg-[#121418] px-4 py-4 text-center">
              <p className="font-mono text-[9px] tracking-[0.3em] text-[#5F6573]">{k}</p>
              <p className="mt-1 font-mono text-[11px] font-bold tracking-widest text-[#F4F5F7]">{v}</p>
            </div>
          ))}
        </div>
      </Reveal>
    </header>

    <section data-testid="plugin-pillars" className="mx-auto max-w-[1400px] px-6 py-24 lg:px-10">
      <div className="grid gap-6 md:grid-cols-3">
        {PLUGIN_PILLARS.map((p, i) => (
          <Reveal key={p.code} delay={0.08 * i}>
            <div className="group steel-panel h-full p-8 transition-[border-color,box-shadow] duration-500 hover:border-[#E52E2E]/50 hover:shadow-[0_0_40px_rgba(229,46,46,0.08)]">
              <p className="font-mono text-sm tracking-[0.3em] text-[#5F6573] transition-colors duration-500 group-hover:text-[#E52E2E]">
                {p.code}
              </p>
              <h3 className="mt-5 font-display text-2xl font-bold uppercase tracking-wide text-[#F4F5F7]">{p.title}</h3>
              <p className="mt-4 text-sm leading-relaxed text-[#9EA4B0]">{p.body}</p>
              <span className="mt-6 block h-px w-0 bg-[#E52E2E] transition-[width] duration-700 group-hover:w-full" />
            </div>
          </Reveal>
        ))}
      </div>
    </section>

    <section data-testid="plugin-suite" className="border-t border-[#282D37]/70 bg-[#0D0F12]">
      <div className="mx-auto max-w-[1400px] px-6 py-24 lg:px-10">
        <Reveal>
          <p className="font-mono text-xs tracking-[0.35em] text-[#E52E2E]">THE ARSENAL SUITE</p>
          <h2 className="mt-4 font-display text-4xl font-black uppercase tracking-tight text-[#F4F5F7] sm:text-5xl">
            Proprietary systems, live in production
          </h2>
        </Reveal>
        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {PLUGIN_SUITE.map((p, i) => (
            <Reveal key={p.name} delay={0.05 * i}>
              <div
                data-testid={`plugin-card-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
                className="group h-full border border-[#282D37] bg-[#121418] p-7 transition-[border-color] duration-500 hover:border-[#E52E2E]/50"
              >
                <Puzzle size={20} className="text-[#E52E2E]" />
                <h3 className="mt-4 font-display text-lg font-bold uppercase tracking-wide text-[#F4F5F7] sm:text-xl">
                  {p.name}
                </h3>
                <p className="mt-1 font-mono text-[9px] tracking-[0.3em] text-[#E52E2E]">{p.domain}</p>
                <p className="mt-3 text-sm leading-relaxed text-[#9EA4B0]">{p.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section data-testid="plugin-pipeline" className="mx-auto max-w-[1400px] px-6 py-24 lg:px-10">
      <div className="grid gap-12 lg:grid-cols-[1fr_1.4fr] lg:gap-20">
        <Reveal>
          <p className="font-mono text-xs tracking-[0.35em] text-[#E52E2E]">RELEASE DISCIPLINE</p>
          <h2 className="mt-4 font-display text-4xl font-black uppercase leading-tight tracking-tight text-[#F4F5F7] sm:text-5xl">
            Stabilised on the Gold Master. Rolled across the network.
          </h2>
          <p className="mt-6 text-sm leading-relaxed text-[#9EA4B0] sm:text-base">
            Every plugin is hardened on the Arsenal 2X reference server before network deployment.
            Code is managed network-wide; gameplay configuration stays server-specific. A deployment
            never overwrites a server's identity.
          </p>
        </Reveal>
        <Reveal delay={0.15}>
          <ol className="space-y-px">
            {DEPLOY_PIPELINE.map((step, i) => (
              <li
                key={step}
                data-testid={`pipeline-step-${i}`}
                className="group flex items-center gap-6 border border-[#282D37] bg-[#121418] px-6 py-4 transition-[border-color] duration-300 hover:border-[#E52E2E]/40"
              >
                <span className="font-mono text-xs font-bold tracking-[0.2em] text-[#E52E2E]">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="font-mono text-xs font-bold tracking-[0.25em] text-[#F4F5F7]">{step}</span>
                {i < DEPLOY_PIPELINE.length - 1 && (
                  <span className="ml-auto hidden text-[#3A404E] sm:block">↓</span>
                )}
              </li>
            ))}
          </ol>
        </Reveal>
      </div>
    </section>

    <section data-testid="plugin-network" className="relative overflow-hidden border-t border-[#282D37]/70 bg-[#0D0F12]">
      <EmberCanvas density={12} className="pointer-events-none absolute inset-0 h-full w-full opacity-30" />
      <div className="relative mx-auto max-w-[1400px] px-6 py-24 lg:px-10">
        <Reveal>
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <p className="font-mono text-xs tracking-[0.35em] text-[#E52E2E]">LIVE DEPLOYMENT</p>
              <h2 className="mt-4 font-display text-4xl font-black uppercase tracking-tight text-[#F4F5F7] sm:text-5xl">
                The Arsenal Rust network
              </h2>
            </div>
            <a
              href="https://arsenalservers.co/servers"
              target="_blank"
              rel="noreferrer"
              data-testid="plugin-network-link"
              className="group flex items-center gap-2 font-mono text-xs font-bold tracking-[0.25em] text-[#9EA4B0] transition-colors duration-300 hover:text-[#E52E2E]"
            >
              VIEW LIVE SERVERS
              <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
          </div>
        </Reveal>
        <div className="mt-12 grid gap-px border border-[#282D37] bg-[#282D37] sm:grid-cols-2 lg:grid-cols-5">
          {RUST_NETWORK.map((s, i) => (
            <Reveal key={s.name} delay={0.06 * i}>
              <div data-testid={`network-server-${i}`} className="h-full bg-[#0D0F12] p-6">
                <div className="flex items-center justify-between">
                  <span className="h-2 w-2 animate-ember-pulse rounded-full bg-[#E52E2E]" />
                  <span className="font-mono text-[9px] tracking-[0.3em] text-[#5F6573]">{s.region}</span>
                </div>
                <p className="mt-4 font-display text-lg font-bold uppercase tracking-wide text-[#F4F5F7]">{s.name}</p>
                <p className="mt-1.5 font-mono text-[9px] leading-relaxed tracking-[0.2em] text-[#9EA4B0]">{s.mode}</p>
                {s.role && (
                  <p className="mt-3 inline-block border border-[#E52E2E]/40 px-2 py-1 font-mono text-[8px] tracking-[0.25em] text-[#E52E2E]">
                    {s.role}
                  </p>
                )}
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section data-testid="plugin-marketplace" className="mx-auto max-w-[1400px] px-6 py-24 lg:px-10">
      <Reveal>
        <div className="relative overflow-hidden border border-[#282D37] bg-[#0D0F12] p-10 text-center sm:p-16">
          <div
            className="pointer-events-none absolute inset-0"
            style={{
              background: "radial-gradient(ellipse 55% 60% at 50% 115%, rgba(229,46,46,0.12) 0%, transparent 70%)",
            }}
          />
          <p className="relative font-mono text-xs tracking-[0.35em] text-[#E52E2E]">ON THE ANVIL</p>
          <h2 className="relative mx-auto mt-4 max-w-2xl font-display text-3xl font-black uppercase leading-tight tracking-tight text-[#F4F5F7] sm:text-5xl">
            The Foundry Marketplace
          </h2>
          <p className="relative mx-auto mt-5 max-w-xl text-sm leading-relaxed text-[#9EA4B0] sm:text-base">
            Arsenal-forged plugins, battle-tested on a live network — available beyond Arsenal in the
            future. Register your interest and be first through the gate.
          </p>
          <Link
            to="/contact"
            data-testid="marketplace-cta"
            className="group relative mt-8 inline-flex items-center gap-3 border border-[#3A404E] px-8 py-4 font-mono text-sm font-bold uppercase tracking-[0.2em] text-[#F4F5F7] transition-colors duration-300 hover:border-[#E52E2E] hover:text-[#E52E2E]"
          >
            Register Interest
            <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </div>
      </Reveal>
    </section>
  </div>
);

export default Plugins;
